import React, { useState, Fragment, useEffect, useMemo, useRef } from "react";
import { Table, Row, Col, Card, CardBody, Input, Label } from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { showToast } from "../ToastNotification";
import CommonLoading from "./CommonLoading";
import {
  formatDate,
  getCurrentFiscalYearDates,
  getQuickRangeDates,
} from "./WorkingDateRange";
import { useVirtualizer } from "@tanstack/react-virtual";

const TableContainerDateRangeFilter5 = ({
  footer,
  // type,
  summary,
  columns,
  data,
  theadClass = "",
  tbodyClass = "",
  onDateRangeChange,
  dateRange,
  setDateRange,
  transactionData,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [summaryData, setSummarydata] = useState(summary);
  const [sorting, setSorting] = useState([]);
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();

  const [tableLoading, setTableLoading] = useState(false);

  const convertToDateFormat = (dateString) => {
    const months = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const [day, month, year] = dateString.replace(",", "").split(" ");
    return `${year}-${months[month]}-${day.padStart(2, "0")}`;
  };

  // console.log("summary", summary);

  const table = useReactTable({
    columns,
    data: transactionData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  // const initialRange =
  //   summary?.start && summary?.end
  //     ? [convertToDateFormat(summary?.start), convertToDateFormat(summary?.end)]
  //     : [];

  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Parse the date in the format "28 Dec, 2024"
    const [day, monthStr, year] = dateString.replace(",", "").split(" ");
    const months = {
      January: 0,
      February: 1,
      March: 2,
      April: 3,
      May: 4,
      June: 5,
      July: 6,
      August: 7,
      September: 8,
      October: 9,
      November: 10,
      December: 11,
    };
    return new Date(year, months[monthStr], parseInt(day));
  };

  const initialRange = useMemo(() => {
    if (summary?.start && summary?.end) {
      return [parseDate(summary.start), parseDate(summary.end)];
    }
    return [];
  }, [summary]);

  // states for options for date filter
  const [selectedRange, setSelectedRange] = useState("this_month"); // default Year Till Date

  const dateOptions = [
    { label: "Today", value: "today" },
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Year Till Date", value: "this_month" },
    { label: "Custom Date", value: "custom" },
  ];

  const handleApplyClick = () => {
    const formatDate = (date) => {
      return `${date?.getFullYear()}-${String(date?.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date?.getDate()).padStart(2, "0")}`;
    };

    if (fromDate && toDate) {
      // if (fromDate.getTime() === toDate.getTime()) {
      //   showToast("Start and End dates cannot be the same.", "error");
      //   return;
      // }

      setTableLoading(true);
      if (onDateRangeChange) {
        onDateRangeChange({
          start_date: formatDate(fromDate),
          end_date: formatDate(toDate),
          setTableLoading,
        });
      }
    } else {
      showToast("Please select both From and To dates.", "error");

      // Fallback to summary range if available
      if (summary?.start && summary?.end) {
        const parsedStart = parseDate(summary.start);
        const parsedEnd = parseDate(summary.end);
        onDateRangeChange({
          start_date: formatDate(parsedStart),
          end_date: formatDate(parsedEnd),
          setTableLoading,
        });
      }
    }
  };

  const tableContainerRef = useRef(null);

  const rows = table.getRowModel().rows;

  // Virtual row configuration
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside visible area
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  useEffect(() => {
    const totals = getRowModel()?.rows.reduce(
      (acc, item) => {
        acc.debit += +item?.original.debit || 0;
        acc.credit += +item?.original.credit || 0;
        acc.cumulative_balance += +item?.original.cumulative_balance || 0;

        return acc;
      },
      {
        debit: 0,
        credit: 0,
        cumulative_balance: 0,
      }
    );
    // console.log("total", totals);
    // console.log("data", getRowModel().rows)
    setSummarydata(totals);
  }, [getRowModel, globalFilter, tableLoading]);
  const isNumeric = (value) => {
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      // Remove commas (if any) before checking.
      return !isNaN(Number(value.replace(/,/g, "")));
    }
    return false;
  };

  return (
    <Fragment>
      {/* Search and Date Filter */}
      <Row className="mb-2">
        <div className="search-box d-inline-block col-xxl-3 col-xl-4 col-lg-7 col-12 mb-xxl-0 mb-lg-2 mb-2">
          <input
            id="search-bar-0"
            type="text"
            style={{
              backgroundColor: "#F1F4F7",
              border: "1px solid transparent",
            }}
            className="form-control border-0 search"
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          <i
            className="bx bx-search-alt search-icon"
            style={{
              height: "38px",
            }}
          ></i>
        </div>
        <Col
          xl={selectedRange === "custom" ? "6" : "4"}
          xxl={selectedRange === "custom" ? "6" : "2"}
          lg={selectedRange === "custom" ? "7" : "7"}
          md={selectedRange === "custom" ? "7" : "12"}
          sm="12"
          className="mb-2 mb-xxl-0"
        >
          <div
            className="flex-shrink-0 flex-grow-1"
            style={{ minWidth: "150px" }}
          >
            <Input
              type="select"
              id="type"
              name="type"
              className="form-control w-100"
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
                height: "38px",

                cursor: "pointer",
              }}
              value={selectedRange}
              onChange={(e) => {
                const dateValue = e.target.value;
                setSelectedRange(dateValue);

                if (dateValue !== "custom") {
                  // Auto-fill dates for quick range
                  const { start, end, formattedStart, formattedEnd } =
                    getQuickRangeDates(dateValue);
                  setDateRange({
                    start_date: formattedStart,
                    end_date: formattedEnd,
                  });
                  setFromDate(start);
                  setToDate(end);
                }
              }}
            >
              {dateOptions.map((opt, idx) => (
                <option key={idx} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </div>
          {selectedRange === "custom" && (
            <>
              <div className="d-flex align-items-center gap-2 flex-fill mt-2 flex-wrap">
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <Label
                    htmlFor="type"
                    className="form-label d-flex align-items-center m-0  text-dark fs-14 fw-medium"
                  >
                    From:
                  </Label>

                  <div className="position-relative w-100">
                    <Flatpickr
                      style={{
                        backgroundColor: "#F1F4F7",
                        border: "1px solid transparent",

                        paddingRight: "30px",
                      }}
                      options={{
                        dateFormat: "d-m-Y",
                        minDate: fyStart,
                        maxDate: new Date(
                          Math.min(
                            fyEnd,
                            new Date(new Date().setDate(new Date().getDate()))
                          )
                        ),
                      }}
                      value={fromDate}
                      onChange={(dates) => {
                        const selected = dates[0];
                        setFromDate(selected);

                        // FIX: update dateRange
                        setDateRange((prev) => ({
                          ...prev,
                          start_date: formatDate(selected),
                        }));

                        if (
                          toDate &&
                          selected &&
                          toDate.getTime() < selected.getTime()
                        ) {
                          setToDate(null);
                        }
                      }}
                      className="form-control"
                      placeholder="dd-mm-yyy"
                    />
                    <i className="bx bx-calendar d-flex h-100 align-items-center position-absolute date_range_icon"></i>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <Label
                    htmlFor="type"
                    className="form-label d-flex align-items-center m-0  text-dark fs-14 fw-medium"
                  >
                    To:
                  </Label>
                  <div className="position-relative w-100">
                    <Flatpickr
                      style={{
                        backgroundColor: "#F1F4F7",
                        border: "1px solid transparent",
                        paddingRight: "30px",
                      }}
                      options={{
                        dateFormat: "d-m-Y",
                        minDate: fromDate || fyStart,
                        maxDate: new Date(
                          Math.min(
                            fyEnd,
                            new Date(new Date().setDate(new Date().getDate()))
                          )
                        ),
                      }}
                      value={toDate}
                      onChange={(dates) => {
                        const selected = dates[0];
                        setToDate(selected);

                        // FIX: update dateRange
                        setDateRange((prev) => ({
                          ...prev,
                          end_date: formatDate(selected),
                        }));
                      }}
                      className="form-control"
                      placeholder="dd-mm-yyyy"
                    />

                    <i className="bx bx-calendar d-flex h-100 align-items-center position-absolute date_range_icon"></i>
                  </div>
                </div>
              </div>
            </>
          )}
        </Col>
        <Col xl="2" xxl="1" lg="2" md="3" sm="4">
          <div className="">
            <button
              className="btn btn-primary w-100"
              onClick={handleApplyClick}
              disabled={tableLoading}
            >
              Apply
            </button>
          </div>
        </Col>
      </Row>

      {/* Table Container */}
      <div className="table-responsive table-container">
        <div
          ref={tableContainerRef}
          style={{
            maxHeight: "70vh",
            overflow: "auto",
            position: "relative",
          }}
        >
          <table className="table table-hover align-middle table-nowrap table-bordered">
            <thead
              className={`bg-gl-primary sticky-header ${theadClass}`}
              style={{
                position: "sticky",
                top: -1,
                zIndex: 2,
                background: "inherit",
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        style={{
                          textAlign: header.column.columnDef.headerTextAlign,
                          cursor: "pointer",
                          whiteSpace: "normal",
                          padding: "0.3rem",
                          paddingLeft: "0.4rem",
                        }}
                        // onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          {flexRender(
                            header.column.columnDef.Header,
                            header.getContext()
                          )}
                          {/* {isSorted === "asc" ? (
                          <i className="ms-2 bx bx-caret-up"></i>
                        ) : isSorted === "desc" ? (
                          <i className="ms-2 bx bx-caret-down"></i>
                        ) : (
                          <i className="ms-2 bx bxs-sort-alt"></i>
                        )} */}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {tableLoading ? (
              <tbody>
                <tr>
                  <td
                    colSpan={columns?.length}
                    className="p-1"
                    style={{ textAlign: "center" }}
                  >
                    <div className="card m-0 text-muted">
                      <div className="card-body centerContent">
                        {CommonLoading && (
                          <CommonLoading
                            style={{
                              maxHeight: "6vh",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <>
                <tbody className={tbodyClass}>
                  {transactionData?.length > 0 ? (
                    rows.length > 0 ? (
                      <>
                        {paddingTop > 0 && (
                          <tr>
                            <td
                              colSpan={columns?.length}
                              style={{ height: `${paddingTop}px`, padding: 0 }}
                            />
                          </tr>
                        )}
                        {virtualRows.map((virtualRow) => {
                          const row = rows[virtualRow.index];
                          return (
                            <tr
                              key={row.id}
                              style={
                                row.original.isGroupSummary
                                  ? {
                                      backgroundColor: "#F1F4F7",
                                      borderColor: "#F1F4F7",
                                    }
                                  : {}
                              }
                              className={
                                row.original.isGroupSummary
                                  ? "group-summary"
                                  : ""
                              }
                            >
                              {row.getVisibleCells().map((cell) => {
                                let content;
                                if (row.original.isGroupSummary) {
                                  // For group summary rows, display the raw value from row.original
                                  // and format it if it's numeric.
                                  let groupValue = row.original[cell.column.id];
                                  if (isNumeric(groupValue)) {
                                    groupValue = Number(
                                      String(groupValue).replace(/,/g, "")
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    });
                                  }
                                  content = groupValue;
                                } else {
                                  // Normal rendering for non-summary rows.
                                  const rawValue = cell.getValue();
                                  let renderedContent = flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  );
                                  // For non-summary rows, if the raw value is numeric, format it.
                                  if (isNumeric(rawValue)) {
                                    const numericValue = Number(
                                      String(rawValue).replace(/,/g, "")
                                    );
                                    renderedContent =
                                      numericValue.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      });
                                  }
                                  content = renderedContent;
                                }
                                return (
                                  <td
                                    key={cell.id}
                                    className="fs-p-bg"
                                    style={{
                                      textAlign:
                                        cell.column.columnDef.bodyTextAlign,
                                      fontWeight: row.original.isGroupSummary
                                        ? 600
                                        : 500,
                                      // whiteSpace: "normal",
                                      padding: "0.3rem",
                                      paddingLeft: "0.4rem",
                                    }}
                                  >
                                    {content}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        {paddingBottom > 0 && (
                          <tr>
                            <td
                              colSpan={columns?.length}
                              style={{
                                height: `${paddingBottom}px`,
                                padding: 0,
                              }}
                            />
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={columns?.length}
                          className="p-1"
                          style={{ textAlign: "center" }}
                        >
                          <div className="card m-0 text-muted">
                            <div className="card-body">No Record Found</div>
                          </div>
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={columns?.length}
                        className="p-1"
                        style={{ textAlign: "center" }}
                      >
                        <div className="card m-0 text-muted">
                          <div className="card-body">No Record Found</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>

                <tfoot
                  className="table-footer sticky-footer"
                  style={{
                    position: "sticky",
                    bottom: -1,
                    zIndex: 2,
                    background: "inherit",
                  }}
                >
                  <tr className="bg-gl-success">
                    <th style={{ textAlign: "start" }}>CLIENT TOTALS</th>
                    <th colSpan={6} style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summary?.debit ?? 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summary?.credit ?? 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {parseFloat(summary?.total_net_qty ?? 0).toLocaleString("en-IN")} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summary?.cumulative_balance ?? 0).toLocaleString(
                        "en-IN",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}
                    </th>
                  </tr>
                </tfoot>
              </>
            )}
          </table>
        </div>
      </div>
    </Fragment>
  );
};

export default TableContainerDateRangeFilter5;
